import { throttle } from 'lodash-es';

/**
 * v-resize
 * ------------------------------------------------------------
 * 用于监听元素尺寸变化的 Vue 3 自定义指令（基于 ResizeObserver）。
 *
 * 特性：
 * - 使用 ResizeObserver 监听元素 size 变化
 * - 内置 lodash-es throttle（默认 200ms）
 * - 支持 box-sizing: border-box（优先使用 borderBoxSize）
 * - 支持立即执行一次（.immediate）
 * - 支持 enabled 开关动态启停监听
 * - 自动清理 observer / 节流函数
 *
 * ------------------------------------------------------------
 *
 * 【基础用法】
 *
 * @example
 * <div v-resize="onResize" />
 *
 * @example
 * <div v-resize.immediate="onResize" />
 *
 * @param {Function} onResize
 * 回调函数，在元素尺寸变化时触发
 *
 * @param {Object} size
 * @param {number} size.width  元素当前宽度（border-box）
 * @param {number} size.height 元素当前高度（border-box）
 *
 * ------------------------------------------------------------
 *
 * 【对象用法（推荐，支持开关）】
 *
 * @example
 * <div
 *   v-resize.immediate="{
 *     enabled: isEnabled,
 *     handler: onResize
 *   }"
 * />
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true]
 * 是否启用监听（支持响应式）
 *
 * @param {Function} options.handler
 * 尺寸变化回调函数
 *
 * ------------------------------------------------------------
 *
 * 【修饰符】
 *
 * @modifier immediate
 * 绑定后立即执行一次回调（同样受节流控制）
 *
 * ------------------------------------------------------------
 *
 * 【注意事项】
 *
 * - 回调触发频率高，避免在回调中执行重计算或接口请求
 * - ResizeObserver 在 IE 中不支持
 * - 建议配合节流后的回调使用（已内置）
 *
 * ------------------------------------------------------------
 *
 * @see https://developer.mozilla.org/zh-CN/docs/Web/API/ResizeObserver
 */

const THROTTLE_DELAY = 200;

function createObserver(el, handler, immediate) {
  const throttledHandler = throttle(handler, THROTTLE_DELAY, {
    leading: true,
    trailing: true
  });

  const observer = new ResizeObserver((entries) => {
    const entry = entries[0];

    let width, height;

    if (entry.borderBoxSize) {
      const boxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;

      width = boxSize.inlineSize;
      height = boxSize.blockSize;
    } else {
      const rect = el.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }

    throttledHandler({ width, height });
  });

  observer.observe(el);

  if (immediate) {
    const rect = el.getBoundingClientRect();
    throttledHandler({ width: rect.width, height: rect.height });
  }

  el.__resizeObserver__ = observer;
  el.__resizeHandler__ = throttledHandler;
}

function destroyObserver(el) {
  el.__resizeObserver__?.disconnect();
  el.__resizeHandler__?.cancel?.();

  delete el.__resizeObserver__;
  delete el.__resizeHandler__;
}

export default {
  mounted(el, binding) {
    const { value, modifiers } = binding;

    // 兼容 v-resize="fn"
    if (typeof value === 'function') {
      createObserver(el, value, modifiers.immediate);
      return;
    }

    // 对象写法 v-resize="{ enabled, handler }"
    const { enabled = true, handler } = value || {};

    if (enabled && typeof handler === 'function') {
      createObserver(el, handler, modifiers.immediate);
    }
  },

  updated(el, binding) {
    const { value, oldValue, modifiers } = binding;

    const wasEnabled = typeof oldValue === 'object' ? oldValue?.enabled !== false : true;

    const isEnabled = typeof value === 'object' ? value?.enabled !== false : true;

    // enabled 从 false -> true
    if (!wasEnabled && isEnabled) {
      const handler = typeof value === 'function' ? value : value?.handler;

      if (typeof handler === 'function') {
        createObserver(el, handler, modifiers.immediate);
      }
    }

    // enabled 从 true -> false
    if (wasEnabled && !isEnabled) {
      destroyObserver(el);
    }
  },

  unmounted(el) {
    destroyObserver(el);
  }
};
